<template>
  <Transition name="fade">
    <Dialog
      :title="modalTitle"
      :show-cancel-button="false"
      :show-save-button="false"
      :custom-footer="true"
      :show-footer="true"
      :show-close-button="true"
      :sm="true"
      @closeEvent="closeEvent"
    >
      <template #modal-content>
        <!-- Delivery Request Date Section -->
        <h3 class="text-lg font-semibold mb-4">Delivery Request Date</h3>
          <Datepicker
            class="w-full"
            v-model="delivery_request_date"
            :min-date="new Date()"
            format="dd/MM/yyyy"
            placeholder="Choose a date"
        />
        <p class="text-sm text-gray-600 mt-3 leading-relaxed">
            The selected delivery date is a requested target date and does not constitute a guaranteed delivery commitment.
            Delivery timelines are subject to timely sample approval, order size, production scheduling, and external shipping conditions.
        </p>
      </template>
      <template #footer>
        <button
            @click="saveShippingDate()"
            class="px-3 py-2 bg-[#219f84] text-white rounded-md mr-2"
            :class="{ 'opacity-50 cursor-not-allowed': isSaveDisabled }"
            :disabled="isSaveDisabled"
        >
            Save
        </button>
        <button @click="closeEvent" class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md">
            Cancel
        </button>
      </template>
    </Dialog>
  </Transition>
</template>
<script setup lang="ts">
import Datepicker from '@/components/Shared/Datepicker.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { showAlert, showToast } from '@/composables/swal';
import { format, isValid, parseISO } from 'date-fns';
import { computed, ref } from 'vue';
interface Props {
  delivery_request_date?: Date | string | null;
  orderItemId?: number | string | null;
  is_exists_request_delivery_date: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  delivery_request_date: null,
  orderItemId: null,
  is_exists_request_delivery_date: false
});

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'success', data?: any): void
}>();

const delivery_request_date = ref<Date | string | null>(props.delivery_request_date);
const order_item_id = ref<number | string | null>(props.orderItemId);
const delivery_dates = ref<string[]>([]);
const isExistsDeliveryRequestDate = ref<boolean>(props.is_exists_request_delivery_date);

const modalTitle = computed(() => {
  return "Delivery Request";
});

const isSaveDisabled = computed(() => {
  return isExistsDeliveryRequestDate.value;
});

const closeEvent = () => {
  emit('close');
};

const saveShippingDate = async () => {
    if (!delivery_request_date.value) {
        showAlert('ERROR', 'Please select a Delivery Request date', 'error');
        return;
    }

    let formattedDate: string;
    const dateValue = delivery_request_date.value;

    if (dateValue instanceof Date) {
        formattedDate = format(dateValue, 'yyyy-MM-dd');
    } else if (typeof dateValue === 'string') {
        const parsedDate = parseISO(dateValue);
        if (!isValid(parsedDate)) {
        showAlert('ERROR', 'Invalid date format', 'error');
        return;
        }
        formattedDate = format(parsedDate, 'yyyy-MM-dd');
    } else {
        showAlert('ERROR', 'Invalid date value', 'error');
        return;
    }

    delivery_dates.value = [...delivery_dates.value, formattedDate];

  try {
    const response = await http.post(`customer-orders/${order_item_id.value}/delivery-request-date`, { delivery_request_dates: delivery_dates.value });
    showToast(response?.data?.message ?? response?.data?.message, 'success');
    emit('success', response?.data);
    closeEvent();
  } catch (err) {
    handleResponseException(err);
  }
};

</script>
<style lang="sass" scoped></style>
